// hooks/use-solana-balances.ts

import { useState, useEffect } from "react";

interface UseSolanaBalancesProps {
  walletAddress: string;
  refetchInterval?: number;
  enabled?: boolean;
  userWallets?: {
    ethereum?: string;
    bsc?: string;
    polygon?: string;
    bitcoin?: string;
    solana?: string;
  };
}

// Solana token mints
const SOLANA_TOKEN_MINTS = {
  SOL: "So11111111111111111111111111111111111111112",
  USDT: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
  USDC: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
  FIXERCOIN: "H4qKn8FMFha8jJuj8xMryMqRhH3h7GjLuxw7TVixpump",
  PINGX: "7KS4DgKHmgSWYC4uGnSozLUon2bDEj6WKhRNSosmpump",
  LOCKER: "EN1nYrW6375zMPUkpkGyGSEXW8WmAqYu4yhf6xnGpump",
  FXM: "7Fnx57ztmhdpL1uAGmUY1ziwPG2UDKmG6poB4ibjpump",
  BONK: "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263",
  JUP: "JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN",
};

const SOLANA_MINT_TO_SYMBOL: Record<string, string> = {
  [SOLANA_TOKEN_MINTS.SOL]: "SOL",
  [SOLANA_TOKEN_MINTS.USDT]: "USDT",
  [SOLANA_TOKEN_MINTS.USDC]: "USDC",
  [SOLANA_TOKEN_MINTS.FIXERCOIN]: "FIXERCOIN",
  [SOLANA_TOKEN_MINTS.PINGX]: "PINGX",
  [SOLANA_TOKEN_MINTS.LOCKER]: "LOCKER",
  [SOLANA_TOKEN_MINTS.FXM]: "FXM",
  [SOLANA_TOKEN_MINTS.BONK]: "BONK",
  [SOLANA_TOKEN_MINTS.JUP]: "JUP",
};

export function useSolanaBalances({
  walletAddress,
  refetchInterval = 30000,
  enabled = true,
  userWallets = {},
}: UseSolanaBalancesProps) {
  const [balances, setBalances] = useState<Record<string, number>>({
    // Multi-chain tokens (Ethereum, BSC, Polygon, Bitcoin)
    BTC: 0,
    ETH: 0,
    BNB: 0,
    MATIC: 0,
    DAI: 0,
    UNI: 0,
    LINK: 0,
    CAKE: 0,
    SHIB: 0, // Added Shiba Inu (Ethereum network)
    // Solana tokens
    SOL: 0,
    USDT: 0,
    USDC: 0,
    FIXERCOIN: 0,
    PINGX: 0,
    LOCKER: 0,
    FXM: 0,
    BONK: 0,
    JUP: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const makeRpcCall = async (method: string, params: any[]) => {
    const payload = {
      jsonrpc: "2.0",
      id: Date.now().toString(),
      method,
      params,
    };

    try {
      const res = await fetch("/api/rpc", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        console.warn(`[Balances] RPC proxy failed (${res.status}), using fallback`);
        return await makeRpcCallDirect("https://api.mainnet-beta.solana.com", payload);
      }

      const data = await res.json();

      if (data.error) {
        throw new Error(data.error.message || "RPC error");
      }

      return data.result;
    } catch (error) {
      console.warn("[Balances] RPC proxy error, using fallback:", error);
      return await makeRpcCallDirect("https://api.mainnet-beta.solana.com", payload);
    }
  };

  const makeRpcCallDirect = async (rpcUrl: string, payload: any) => {
    const res = await fetch(rpcUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error(`RPC HTTP error: ${res.status}`);
    }

    const data = await res.json();

    if (data.error) {
      throw new Error(data.error.message || "RPC error");
    }

    return data.result;
  };

  // Fetch multi-chain balances from backend API
  const fetchMultiChainBalances = async () => {
    try {
      const response = await fetch('/api/multi-chain-balances', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          addresses: {
            ethereum: userWallets?.ethereum || null,
            bsc: userWallets?.bsc || null,
            polygon: userWallets?.polygon || null,
            bitcoin: userWallets?.bitcoin || null,
          }
        })
      });
      
      if (!response.ok) {
        console.warn("[MultiChain] API returned error:", response.status);
        return {};
      }
      
      const data = await response.json();
      if (data.success && data.balances) {
        console.log("[MultiChain] Balances fetched:", data.balances);
        return data.balances;
      }
      return {};
    } catch (error) {
      console.error("[MultiChain] Fetch error:", error);
      return {};
    }
  };

  // Fetch Solana token balances
  const fetchSolanaBalances = async () => {
    if (!walletAddress || !enabled) {
      return {
        SOL: 0,
        USDT: 0,
        USDC: 0,
        FIXERCOIN: 0,
        PINGX: 0,
        LOCKER: 0,
        FXM: 0,
        BONK: 0,
        JUP: 0,
      };
    }

    try {
      console.log("[Balances] Fetching Solana balances for:", walletAddress);

      let solRes;
      try {
        // SOL balance
        solRes = await makeRpcCall("getBalance", [walletAddress]);
      } catch (err) {
        console.warn("[Balances] RPC call failed, using fallback balance of 0");
        solRes = { value: 0 };
      }

      const sol = (solRes?.value || 0) / 1e9;

      const newBalances: Record<string, number> = {
        SOL: sol,
        USDT: 0,
        USDC: 0,
        FIXERCOIN: 0,
        PINGX: 0,
        LOCKER: 0,
        FXM: 0,
        BONK: 0,
        JUP: 0,
      };

      // Fetch all token balances in parallel
      await Promise.all(
        Object.entries(SOLANA_TOKEN_MINTS).map(async ([symbol, mint]) => {
          if (symbol === "SOL") return;

          try {
            const res = await makeRpcCall("getTokenAccountsByOwner", [
              walletAddress,
              { mint },
              { encoding: "jsonParsed" },
            ]);

            const account = res?.value?.[0];
            const amount = account?.account?.data?.parsed?.info?.tokenAmount?.uiAmount || 0;

            if (amount > 0) {
              newBalances[symbol] = amount;
              console.log(`[Balances] ✅ ${symbol}:`, amount);
            }
          } catch (err) {
            // Silently fail for individual token balance fetches
            newBalances[symbol] = 0;
          }
        })
      );

      return newBalances;
    } catch (err: any) {
      console.warn("[Balances] Solana fetch failed, returning zero balances:", err.message);
      // Return zero balances instead of throwing error
      return {
        SOL: 0,
        USDT: 0,
        USDC: 0,
        FIXERCOIN: 0,
        PINGX: 0,
        LOCKER: 0,
        FXM: 0,
        BONK: 0,
        JUP: 0,
      };
    }
  };

  const fetchBalances = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch Solana balances
      const solanaBalances = await fetchSolanaBalances();
      
      // Fetch multi-chain balances (BTC, ETH, BNB, MATIC, SHIB, etc.)
      const multiChainBalances = await fetchMultiChainBalances();
      
      // Combine all balances
      const allBalances = {
        ...multiChainBalances,
        ...solanaBalances,
      };
      
      console.log("[Balances] Final all balances:", allBalances);
      setBalances(allBalances);
    } catch (err: any) {
      console.error("[Balances] Error:", err);
      setError(err.message || "Failed to fetch balances");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalances();

    if (refetchInterval > 0 && enabled) {
      const interval = setInterval(fetchBalances, refetchInterval);
      return () => clearInterval(interval);
    }
  }, [walletAddress, refetchInterval, enabled, JSON.stringify(userWallets)]);

  return {
    balances,
    loading,
    error,
    refetch: fetchBalances,
  };
}
